import React from 'react';
import PropTypes from 'prop-types';
import {
  Grid,
  TextField,
  FormControl,
  FormHelperText,
  Button,
  ExpansionPanel,
  ExpansionPanelDetails,
  ExpansionPanelSummary,
  Typography,
} from '@material-ui/core';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import { withStyles } from '@material-ui/core/styles';
import { inject, observer, PropTypes as mobxPropTypes } from 'mobx-react';
import { Formik } from 'formik';
import * as yup from 'yup';
import { ROLE_ADMIN } from '../../constants/roles';
import Autocomplete from '../Autocomplete/Autocomplete';
import isMobile from '../../utils/isMobile';


const validations = yup.object().shape({
  logged_user: yup.string(),
  affected_user: yup.string(),
  created_at__gte: yup.string(),
  created_at__lte: yup.string(),
});

const styles = theme => ({
  checkbox: {
    alignItems: 'flex-end',
  },
  button: {
    margin: theme.spacing.unit,
    marginTop: 20,
  },
  root: {
    display: 'block',
  },
  secondaryHeading: {
    fontSize: theme.typography.pxToRem(15),
    color: theme.palette.text.secondary,
  },
  heading: {
    fontSize: theme.typography.pxToRem(18),
    flexBasis: '33.33%',
    flexShrink: 0,
  },
});

const propTypes = {
  classes: PropTypes.oneOfType([PropTypes.object]).isRequired,
  authStore: mobxPropTypes.observableObject.isRequired,
  onSubmit: PropTypes.func.isRequired,
};

class AuditLogSearchForm extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      loggedUsers: [],
      affectedUsers: [],
      expanded: !isMobile(),
    };
    this.handleAutoCompleteLogged = this.handleAutoCompleteLogged.bind(this);
    this.handleAutoCompleteAffected = this.handleAutoCompleteAffected.bind(this);
    this.handleChange = this.handleChange.bind(this);
  }

  componentWillMount() {
    const { authStore } = this.props;
    const currentUser = authStore.getUser;
    if (currentUser.role === ROLE_ADMIN) {
      this.fetchLoggedUsers();
      this.fetchAffectedUsers();
    }
  }

  async fetchUsers(searchParams) {
    const { userStore } = this.props;
    const defaultParams = {
      order_by: 'first_name',
    };
    const params = Object.assign(searchParams, defaultParams);
    const response = await userStore.api.users.get({ params });
    let results = [];
    if (response && response.objects) {
      results = response.objects.map(user => ({
        value: user.id,
        label: user.full_name,
      }));
    }
    return results;
  }

  async fetchLoggedUsers(searchParams = {}) {
    const loggedUsers = await this.fetchUsers(searchParams);
    this.setState({ loggedUsers });
  }

  async fetchAffectedUsers(searchParams = {}) {
    const affectedUsers = await this.fetchUsers(searchParams);
    this.setState({ affectedUsers });
  }

  async handleAutoCompleteLogged(input) {
    this.setState({ cleanAssign: false });
    if (!input) {
      return Promise.resolve([]);
    }
    const loggedUsers = await this.fetchUsers({ full_name: input });
    return Promise.resolve(loggedUsers);
  }

  async handleAutoCompleteAffected(input) {
    this.setState({ cleanAssign: false });
    if (!input) {
      return Promise.resolve([]);
    }
    const affectedUsers = await this.fetchUsers({ full_name: input });
    return Promise.resolve(affectedUsers);
  }

  handleChange(event, expanded) {
    this.setState({ expanded });
  }

  renderForm({
    values,
    touched,
    errors,
    handleChange,
    handleSubmit,
    isSubmitting,
    handleReset,
    setFieldValue,
  }) {
    const { classes } = this.props;
    const { loggedUsers, affectedUsers, cleanAssign } = this.state;
    const handleAutoCompleteChangeLogged = (value) => {
      setFieldValue('logged_user', value);
    };
    const handleAutoCompleteChangeAffected = (value) => {
      setFieldValue('affected_user', value);
    };
    return (
      <form onSubmit={handleSubmit} noValidate>
        <Grid container spacing={24} direction="row">
          <Grid xs={12} md={6} item>
            <FormControl error={Boolean(touched.logged_user && errors.logged_user)} fullWidth>
              <Autocomplete
                defaultOptions={loggedUsers}
                defaultSelected={cleanAssign ? { label: '', value: '' } : null}
                loadOptions={this.handleAutoCompleteLogged}
                onChange={handleAutoCompleteChangeLogged}
                placeholder="Logged User"
                disabled={isSubmitting}
              />
              {touched.logged_user
              && errors.logged_user
              && (
              <FormHelperText error>
                {errors.logged_user}
              </FormHelperText>
              )}
            </FormControl>
          </Grid>
          <Grid xs={12} md={6} item>
            <FormControl error={Boolean(touched.affected_user && errors.affected_user)} fullWidth>
              <Autocomplete
                defaultOptions={affectedUsers}
                defaultSelected={cleanAssign ? { label: '', value: '' } : null}
                loadOptions={this.handleAutoCompleteAffected}
                onChange={handleAutoCompleteChangeAffected}
                placeholder="Affected User"
                disabled={isSubmitting}
              />
              {touched.affected_user
              && errors.affected_user
              && (
              <FormHelperText error>
                {errors.affected_user}
              </FormHelperText>
              )}
            </FormControl>
          </Grid>
          <Grid xs={12} md={3} item>
            <TextField
              name="created_at__gte"
              label="Date From"
              type="date"
              value={values.created_at__gte}
              error={Boolean(touched.created_at__gte && errors.created_at__gte)}
              helperText={
                touched.created_at__gte && errors.created_at__gte ? errors.created_at__gte : ''
              }
              fullWidth
              onChange={handleChange}
              InputLabelProps={{
                shrink: true,
              }}
            />
          </Grid>
          <Grid xs={12} md={3} item>
            <TextField
              name="created_at__lte"
              label="Date To"
              type="date"
              value={values.created_at__lte}
              error={Boolean(touched.created_at__lte && errors.created_at__lte)}
              helperText={
                touched.created_at__lte && errors.created_at__lte ? errors.created_at__lte : ''
              }
              fullWidth
              onChange={handleChange}
              InputLabelProps={{
                shrink: true,
              }}
            />
          </Grid>
          <Grid xs={12} md={3} container item />
          <Grid xs={12} md={3} container item>
            <Grid alignItems="flex-end" justify="flex-end" xs={12} container item>
              <Button
                type="button"
                color="primary"
                disabled={isSubmitting}
                className={classes.button}
                onClick={handleReset}
              >
                Reset
              </Button>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                disabled={isSubmitting}
                className={classes.button}
              >
                Search
              </Button>
            </Grid>
          </Grid>
        </Grid>
      </form>
    );
  }

  render() {
    const { onSubmit, classes } = this.props;
    const { expanded } = this.state;
    let subHeading = null;
    if (isMobile() && !expanded) {
      subHeading = (
        <Typography className={classes.secondaryHeading}>
          Expand to display filters
        </Typography>);
    }
    return (
      <Formik
        initialValues={{
          logged_user: '',
          affected_user: '',
          created_at__gte: '',
          created_at__lte: '',
        }}
        validationSchema={validations}
        onSubmit={async (values, { setSubmitting }) => {
          const filters = { order_by: '-created_at' };
          Object.entries(values).forEach((entry) => {
            const key = entry[0];
            const value = entry[1];
            if (value !== '' && value !== false) {
              filters[key] = value;
            }
          });
          await onSubmit(filters);
          setSubmitting(false);
        }}
        onReset={async (values, { setSubmitting }) => {
          const filters = { order_by: '-created_at' };
          this.setState({ loggedUsers: [], affectedUsers: [], cleanAssign: true });
          await onSubmit(filters);
          setSubmitting(false);
        }}
        render={({
          values,
          touched,
          errors,
          handleChange,
          handleSubmit,
          isSubmitting,
          handleReset,
          setFieldValue,
        }) => {
          const formParams = {
            values,
            touched,
            errors,
            handleChange,
            handleSubmit,
            isSubmitting,
            handleReset,
            setFieldValue,
          };
          const { authStore } = this.props;
          const currentUser = authStore.getUser;
          const form = currentUser.role === ROLE_ADMIN && this.renderForm(formParams);
          return (
            <ExpansionPanel
              defaultExpanded={!isMobile()}
              classes={{ expanded: 'overflowVisible' }}
              onChange={this.handleChange}
            >
              <ExpansionPanelSummary expandIcon={<ExpandMoreIcon />}>
                <Typography className={classes.heading}>
                  Search
                </Typography>
                {subHeading}
              </ExpansionPanelSummary>
              <ExpansionPanelDetails classes={{ root: classes.root }}>
                {form}
              </ExpansionPanelDetails>
            </ExpansionPanel>
          );
        }}
      />
    );
  }
}

AuditLogSearchForm.propTypes = propTypes;
const AuditLogSearchFormInjected = inject('authStore', 'userStore')(
  observer(AuditLogSearchForm),
);
export default withStyles(styles)(AuditLogSearchFormInjected);



// WEBPACK FOOTER //
// ./src/components/AuditLogSearchForm/AuditLogSearchForm.js